import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { Row, Col } from 'antd'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { UserOutlined, IdcardOutlined } from '@ant-design/icons'
import Button from '../Button'
import { TextInput } from './TextInput'
import logo from '../../images/Logo.png'
import packageJson from '../../../package.json'
import http from '../../services/httpServiceHealthCare'

export const ErrMsg = styled.p`
  &&& {
    color: red;
    font-size: 12px;
    margin-top: 4px;
  }
`

const LabelText = styled.h2`
  color: ${(props) => props.theme.primaryText};
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 6px;
  }
`

const Container = styled.div`
  width: 100%;
  max-width: 500px;
  background-color: white;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 30px 20px;
  box-shadow: 0 0 20px 0px rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  margin: 20px auto;
  min-height: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 20px 15px;
    margin: 10px 0;
    border-radius: 0;
    box-shadow: none;
    min-height: auto;
  }
  
  h5 {
    color: ${(props) => props.theme.primaryText};
    font-weight: bold;
    margin: 0;
    font-size: 24px;
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  
  p {
    color: ${(props) => props.theme.primaryText};
    margin: 0;
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  
  .login-link {
    font-size: 14px;
    color: ${(props) => props.theme.primaryButton};
    text-decoration: underline;
    margin-top: 15px;
    
    @media (max-width: 768px) {
      font-size: 13px;
      margin-top: 10px;
    }
  }
`

const VersionText = styled.div`
  margin-top: 15px;
  font-size: 12px;
  color: gray;
  
  @media (max-width: 768px) {
    font-size: 11px;
    margin-top: 10px;
  }
`

const SuccessMessage = styled.div`
  text-align: center;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  h3 {
    color: #52c41a;
    margin-bottom: 15px;
    font-size: 20px;
    
    @media (max-width: 768px) {
      font-size: 18px;
      margin-bottom: 10px;
    }
  }
  
  p {
    color: #666;
    margin-bottom: 20px;
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 14px;
      margin-bottom: 15px;
    }
  }
`

const FormContainer = styled.div`
  width: 100%;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    margin-top: 15px;
  }
`

const validationSchema = Yup.object().shape({
  firstname: Yup.string().required('กรุณากรอกชื่อ'),
  lastname: Yup.string().required('กรุณากรอกนามสกุล'),
  cId: Yup.string().required('กรุณากรอกเลขบัตรประชาชน'),
})

const PatientRegistrationForm = ({ onSubmit, hasSubmitted }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsSubmitting(true)
    try {
      // ตรวจสอบ CID ก่อน
      const checkResponse = await http.get(`/PatientRegistrations/checkCid?cId=${values.cId}`)

      if (checkResponse.data && checkResponse.data.exists) {
        alert('เลขบัตรประชาชนนี้มีอยู่ในระบบแล้ว')
        setIsSubmitting(false)
        setSubmitting(false)
        return
      }

      // ส่งข้อมูลการสมัคร
      const response = await http.post('/PatientRegistrations', {
        firstname: values.firstname,
        lastname: values.lastname,
        cId: values.cId,
        status: 'pending',
        additionalData: {
          source: 'LIFF',
          timestamp: new Date().toISOString()
        }
      })

      if (response.data) {
        onSubmit(true) // ส่งสถานะสำเร็จ
        resetForm()
      }
    } catch (error) {
      console.error('Registration error:', error)
      if (error.response && error.response.status === 409) {
        alert('เลขบัตรประชาชนนี้มีอยู่ในระบบแล้ว')
      } else {
        alert('เกิดข้อผิดพลาดในการสมัคร กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  if (hasSubmitted) {
    return (
      <Container>
        <img src={logo} alt="logo" width={80} height={80} />
        <h5>Vajira@Home</h5>
        <SuccessMessage>
          <h3>ส่งคำขอสมัครสำเร็จ!</h3>
          <p>คำขอสมัครของคุณได้รับการส่งเรียบร้อยแล้ว</p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            เจ้าหน้าที่จะตรวจสอบและติดต่อกลับภายใน 1-3 วันทำการ
          </p>
          <Link to="/login" className="login-link">
            กลับสู่หน้าหลัก
          </Link>
        </SuccessMessage>
        <VersionText>Version {packageJson.version}</VersionText>
      </Container>
    )
  }

  return (
    <Formik
      initialValues={{
        firstname: '',
        lastname: '',
        cId: '',
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(props) => {
        const { values, handleChange, handleSubmit, errors } = props
        const { firstname, lastname, cId } = values

        return (
          <Container>
            <img src={logo} alt="logo" width={80} height={80} />
            <h5>Vajira@Home</h5>
            <p>สมัครสมาชิกผู้ป่วย</p>

            <FormContainer>
              <form className="w-100">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={24} md={24}>
                    <LabelText>
                      <span style={{ color: 'red' }}>*</span>ชื่อ
                    </LabelText>
                    <TextInput
                      placeholder="กรอกชื่อ (ภาษาไทย)"
                      name="firstname"
                      prefix={<UserOutlined />}
                      value={firstname}
                      onChange={handleChange}
                      errors={errors}
                    />
                    <ErrorMessage component={ErrMsg} name="firstname" />
                  </Col>

                  <Col xs={24} sm={24} md={24}>
                    <LabelText>
                      <span style={{ color: 'red' }}>*</span>นามสกุล
                    </LabelText>
                    <TextInput
                      placeholder="กรอกนามสกุล (ภาษาไทย)"
                      name="lastname"
                      prefix={<UserOutlined />}
                      value={lastname}
                      onChange={handleChange}
                      errors={errors}
                    />
                    <ErrorMessage component={ErrMsg} name="lastname" />
                  </Col>

                  <Col xs={24} sm={24} md={24}>
                    <LabelText>
                      <span style={{ color: 'red' }}>*</span>เลขบัตรประชาชน
                    </LabelText>
                    <TextInput
                      placeholder="กรอกเลขบัตรประชาชน"
                      name="cId"
                      prefix={<IdcardOutlined />}
                      value={cId}
                      onChange={handleChange}
                      errors={errors}
                    />
                    <ErrorMessage component={ErrMsg} name="cId" />
                  </Col>

                  <Col xs={24} sm={24} md={24} style={{ marginTop: 20 }}>
                    <Button
                      type="submit"
                      size="large"
                      onClick={handleSubmit}
                      primary
                      disabled={isSubmitting}
                      style={{ width: '100%', height: '50px', fontSize: '16px' }}
                    >
                      {isSubmitting ? 'กำลังส่งข้อมูล...' : 'ส่งคำขอสมัคร'}
                    </Button>
                  </Col>

                  <Col xs={24} sm={24} md={24} style={{ textAlign: 'center', marginTop: 15 }}>
                    <Link to="/login" className="login-link">
                      มีบัญชีแล้ว? เข้าสู่ระบบ
                    </Link>
                  </Col>
                </Row>
              </form>
            </FormContainer>

            <VersionText>Version {packageJson.version}</VersionText>
          </Container>
        )
      }}
    </Formik>
  )
}

PatientRegistrationForm.propTypes = {
  onSubmit: PropTypes.func,
  hasSubmitted: PropTypes.bool,
}

PatientRegistrationForm.defaultProps = {
  onSubmit: () => { },
  hasSubmitted: false,
}

export default PatientRegistrationForm
